/**
 * Clove & Twine — accessibility behaviour layer
 * ---------------------------------------------
 * Adds the keyboard and screen-reader behaviour that the theme's custom
 * navigation, carousels and collection controls are missing.
 *
 * This is ordinary theme code, not an accessibility overlay: it operates on the
 * theme's own markup, adds no widget, and every fix has a matching source
 * change documented in PATCHES.md. Load it after global.js and jQuery.
 *
 * Scope, by complaint paragraph:
 *   §33 Nav-2/3  keyboard-operable submenus + honest aria-expanded
 *   §33 Nav-6    pause control for auto-advancing carousels
 *   §33 Nav-8    focus + status announcement for "Load More"
 *   §33 Nav-9/10 button semantics and expanded state for Sort By / filters
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Shared live region — one polite announcer for the whole document.
   * ------------------------------------------------------------------ */
  var announcer;

  // Counters for generated ids. Declared here rather than beside the code that
  // uses them: `var` hoists as undefined, and `++undefined` is NaN, which would
  // hand every generated id the same "-NaN" suffix.
  var filterGroupCounter = 0;
  var minQtyCounter = 0;

  function announce(message) {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'ct-a11y-announcer';
      announcer.className = 'visually-hidden';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      document.body.appendChild(announcer);
    }
    // Clearing first forces re-announcement of an identical message.
    announcer.textContent = '';
    window.setTimeout(function () {
      announcer.textContent = message;
    }, 100);
  }

  function focusableWithin(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(
        'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
        'select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])'
      ),
      function (el) { return el.getClientRects().length > 0; }
    );
  }

  /* ================================================================== *
   * 1. Navigation submenus — §33 Nav-2 and Nav-3
   *
   * The theme opens submenus with CSS :hover only. Top-level items are
   * anchors carrying a hardcoded aria-expanded="false" that nothing ever
   * updates, so a screen reader says "collapsed" forever and "expanded"
   * never. Enter simply follows the link.
   *
   * We keep the anchor (it is a real destination and mouse users rely on
   * hover) and add a dedicated disclosure button beside it. That is the
   * pattern Shopify's own Dawn header uses, and it keeps "go to the Shop
   * page" and "open the Shop menu" as two distinct, discoverable actions.
   * ================================================================== */
  var OPEN_CLASS = 'ct-submenu-open';
  var uid = 0;

  function panelFor(item) {
    return item.querySelector(':scope > div.megamenu, :scope > ul.dropdown');
  }

  // Only menu items that are actually rendered. navigation.liquid keeps a
  // display:none source copy of the whole menu in `.move-nav`, which jQuery
  // clones into the header; wiring the hidden copy would duplicate every id.
  function navItems() {
    return Array.prototype.filter.call(
      document.querySelectorAll('.header__inline-menu li.header__menu-item'),
      function (li) {
        return li.getClientRects().length && li.querySelector('a.header__menu-item--top');
      }
    );
  }

  function toggleIn(item) {
    return item.querySelector('.ct-submenu-toggle');
  }

  function closeSubmenu(item) {
    var panel = panelFor(item);
    var toggle = toggleIn(item);
    item.classList.remove(OPEN_CLASS);
    if (panel) panel.setAttribute('aria-hidden', 'true');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function closeAllSubmenus(except) {
    navItems().forEach(function (li) {
      if (li !== except) closeSubmenu(li);
    });
  }

  function openSubmenu(item) {
    closeAllSubmenus(item);
    var panel = panelFor(item);
    var toggle = toggleIn(item);
    item.classList.add(OPEN_CLASS);
    if (panel) panel.setAttribute('aria-hidden', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  function initNav() {
    navItems().forEach(function (item) {
      var trigger = item.querySelector('a.header__menu-item--top');
      var panel = panelFor(item);
      if (!trigger || !panel) return;

      // Clone-proof guard: check for a real toggle in the DOM rather than a
      // data-* flag. jQuery's .html() clone copies attributes, so a dataset
      // flag would survive cloning and cause the VISIBLE menu to be skipped.
      if (toggleIn(item)) return;

      item.classList.add('ct-has-toggle');

      // The template's ARIA on the anchor is wrong three ways: the attribute is
      // misspelled (aria-has-popup, not aria-haspopup), aria-controls points at
      // an id that does not exist, and aria-expanded is never updated. Strip it
      // — the anchor is just a link — and put real disclosure semantics on the
      // button below.
      trigger.removeAttribute('aria-has-popup');
      trigger.removeAttribute('aria-haspopup');
      trigger.removeAttribute('aria-expanded');
      trigger.removeAttribute('aria-controls');
      trigger.removeAttribute('aria-label'); // was clobbering the link's own text

      // The template also emits a visually-hidden span for a locale key that
      // does not exist, so it renders "Translation missing: …" into the name.
      item.querySelectorAll('a .visually-hidden').forEach(function (span) {
        if (/Translation missing/i.test(span.textContent)) span.remove();
      });

      var label = (trigger.textContent || '').replace(/\s+/g, ' ').trim();
      if (!panel.id) panel.id = 'ct-submenu-' + ++uid;
      panel.setAttribute('aria-hidden', 'true');

      // Wrapper gives the toggle a containing block. See the long note in
      // ct-a11y.css for why the <li> must NOT be made position:relative.
      var wrap = trigger.parentElement;
      if (!wrap.classList.contains('ct-nav-item')) {
        wrap = document.createElement('span');
        wrap.className = 'ct-nav-item';
        trigger.insertAdjacentElement('beforebegin', wrap);
        wrap.appendChild(trigger);
      }

      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'ct-submenu-toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', panel.id);
      toggle.setAttribute('aria-label', label + ' menu');

      // Move the theme's own caret SVG into the button so rendering is
      // byte-for-byte identical to before — the caret keeps the theme's
      // `.header__menu-item .icon-caret` rules and anchors to the button, which
      // occupies the exact box the caret previously occupied.
      var caret = trigger.querySelector('svg.icon-caret');
      if (caret) {
        caret.setAttribute('aria-hidden', 'true');
        toggle.appendChild(caret);
      } else {
        toggle.innerHTML = '<span class="ct-submenu-toggle__caret" aria-hidden="true"></span>';
      }
      wrap.appendChild(toggle);

      // Child links inherit the PARENT's aria-label in the template, so every
      // child announced as its parent's name. The visible text is correct.
      panel.querySelectorAll('a[aria-label]').forEach(function (link) {
        var text = (link.textContent || '').replace(/\s+/g, ' ').trim();
        if (text && link.getAttribute('aria-label').trim() !== text) {
          link.removeAttribute('aria-label');
        }
      });

      // Megamenu image panels ship with no alt and no link text at all.
      panel.querySelectorAll('a').forEach(function (link) {
        var named = (link.getAttribute('aria-label') || '').trim() ||
          (link.textContent || '').replace(/\s+/g, ' ').trim();
        if (named) return;
        var img = link.querySelector('img');
        var caption = link.querySelector('.caption');
        var fallback = (caption && caption.textContent.trim()) ||
          (img && img.getAttribute('alt')) ||
          deriveNameFromHref(link.getAttribute('href'));
        if (fallback) link.setAttribute('aria-label', fallback);
        if (img && !img.getAttribute('alt')) img.setAttribute('alt', '');
      });
    });
  }

  function deriveNameFromHref(href) {
    if (!href || href === '#') return '';
    var slug = href.split('?')[0].replace(/\/$/, '').split('/').pop();
    if (!slug) return '';
    return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  /* ---- Delegated handlers -------------------------------------------
   * These are bound ONCE to the document rather than to each toggle.
   * navigation.liquid clones the menu into the header with jQuery's .html(),
   * which copies markup but NOT event listeners — per-element handlers were
   * silently lost on the visible menu, so Enter did nothing. Delegation is
   * immune to that.
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('.ct-submenu-toggle');
    if (!toggle) {
      if (!event.target.closest('.header__inline-menu')) closeAllSubmenus(null);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    var item = toggle.closest('li.header__menu-item');
    if (!item) return;
    if (item.classList.contains(OPEN_CLASS)) closeSubmenu(item);
    else openSubmenu(item);
  }, true);

  document.addEventListener('keydown', function (event) {
    var item = event.target.closest('.header__inline-menu li.header__menu-item');
    if (!item) return;

    // Down arrow from the link or the toggle opens and enters the panel.
    if (event.key === 'ArrowDown' &&
        (event.target.closest('.ct-submenu-toggle') ||
         event.target.matches('a.header__menu-item--top'))) {
      var panel = panelFor(item);
      if (!panel) return;
      event.preventDefault();
      openSubmenu(item);
      var first = focusableWithin(panel)[0];
      if (first) first.focus();
      return;
    }

    // Escape closes and returns focus to the toggle.
    if (event.key === 'Escape' && item.classList.contains(OPEN_CLASS)) {
      event.stopPropagation();
      closeSubmenu(item);
      var t = toggleIn(item);
      if (t) t.focus();
    }
  });

  // Tabbing out of an open panel closes it.
  document.addEventListener('focusout', function (event) {
    var item = event.target.closest('.header__inline-menu li.header__menu-item');
    if (!item || !item.classList.contains(OPEN_CLASS)) return;
    window.setTimeout(function () {
      if (!item.contains(document.activeElement)) closeSubmenu(item);
    }, 0);
  });

  /* ================================================================== *
   * 2. Auto-advancing carousels need a pause control — §33 Nav-6
   *
   * "Clients Elevating Their Gifting" (2.5s) and "What Our Clients Are
   * Saying" (7s) autoplay with no way to stop them. Slick's pauseOnHover /
   * pauseOnFocus are mouse-and-focus only and the logo slides contain no
   * focusable children, so keyboard and AT users have no mechanism at all.
   * WCAG 2.2.2 requires one for anything moving longer than five seconds.
   * ================================================================== */
  function initCarouselPauseControls() {
    if (!window.jQuery || !window.jQuery.fn.slick) return;
    var $ = window.jQuery;

    $('.slick-initialized').each(function () {
      var $slider = $(this);
      var slick;
      try { slick = $slider.slick('getSlick'); } catch (e) { return; }
      if (!slick || !slick.options.autoplay) return;

      /* autoplay:true does NOT mean anything actually moves. Several sections
       * carry it on sliders that hold a single slide — /pages/kitbuilder has two,
       * with slideCount 0 and 1 — and those rotate through nothing. Adding a
       * control there put a stray "Pause" button on the page for content that
       * never animates.
       *
       * WCAG 2.2.2 is about moving, blinking or scrolling content, so the test
       * is whether there is more to show than fits: only then is there motion to
       * pause. */
      var visible = slick.options.slidesToShow || 1;
      if (slick.slideCount < 2 || slick.slideCount <= visible) return;

      if ($slider.data('ctPauseAdded')) return;
      $slider.data('ctPauseAdded', true);

      var section = this.closest('.shopify-section') || this.parentNode;
      var heading = section.querySelector('h2, h3');
      var name = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : 'carousel';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ct-carousel-pause';
      btn.setAttribute('aria-label', 'Pause ' + name);
      btn.dataset.state = 'playing';
      btn.innerHTML = '<span class="ct-carousel-pause__icon" aria-hidden="true"></span>' +
        '<span class="ct-carousel-pause__text">Pause</span>';

      btn.addEventListener('click', function () {
        if (btn.dataset.state === 'playing') {
          $slider.slick('slickPause');
          btn.dataset.state = 'paused';
          btn.setAttribute('aria-label', 'Play ' + name);
          btn.querySelector('.ct-carousel-pause__text').textContent = 'Play';
          announce(name + ' paused');
        } else {
          $slider.slick('slickPlay');
          btn.dataset.state = 'playing';
          btn.setAttribute('aria-label', 'Pause ' + name);
          btn.querySelector('.ct-carousel-pause__text').textContent = 'Pause';
          announce(name + ' playing');
        }
      });

      var host = section.querySelector('.ct-carousel-controls') || section;
      host.appendChild(btn);

      // Honour the OS "reduce motion" setting: stop before the first advance.
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        $slider.slick('slickPause');
        btn.dataset.state = 'paused';
        btn.setAttribute('aria-label', 'Play ' + name);
        btn.querySelector('.ct-carousel-pause__text').textContent = 'Play';
      }

      // Slick's arrows are all labelled bare "Previous"/"Next"; on a page with
      // four carousels that is four identical pairs. Qualify them.
      $slider.parent().find('.slick-prev, .slick-next').each(function () {
        var isPrev = this.classList.contains('slick-prev');
        this.setAttribute('aria-label', (isPrev ? 'Previous' : 'Next') + ': ' + name);
      });

      // Slick mislabels its dots ("4 of 3"). Recompute against real slides.
      var dots = $slider.parent().find('.slick-dots button');
      dots.each(function (i) {
        this.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + dots.length);
      });
    });
  }

  /* ================================================================== *
   * 3. "Load More" — §33 Nav-8
   *
   * Activating it appends products and drops focus on <body>, so a screen
   * reader user is thrown to the top of the document with no indication
   * anything happened. Announce the result and move focus to the first new
   * product so the reading position follows the content.
   * ================================================================== */
  function initLoadMore() {
    var grid = document.querySelector('#CollectionProductGrid, #product-grid');
    if (!grid) return;

    var cardSelector = '.card-wrapper, .product-card, li.grid__item';
    var countCards = function () { return grid.querySelectorAll(cardSelector).length; };

    document.addEventListener('click', function (event) {
      var btn = event.target.closest('a, button');
      if (!btn || !/load more/i.test(btn.textContent || '')) return;

      var before = countCards();
      btn.setAttribute('aria-controls', grid.id);
      announce('Loading more products…');

      var attempts = 0;
      var poll = window.setInterval(function () {
        var after = countCards();
        attempts++;

        if (after > before) {
          window.clearInterval(poll);
          var added = after - before;
          var cards = grid.querySelectorAll(cardSelector);
          var firstNew = cards[before];

          announce(added + (added === 1 ? ' more product loaded.' : ' more products loaded.'));

          if (firstNew) {
            if (!firstNew.hasAttribute('tabindex')) firstNew.setAttribute('tabindex', '-1');
            firstNew.focus({ preventScroll: true });
            firstNew.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        } else if (attempts > 40) {
          window.clearInterval(poll);
          announce('No additional products were loaded.');
        }
      }, 250);
    });
  }

  /* ================================================================== *
   * 4. Sort By and filter category controls — §33 Nav-9 and Nav-10
   *
   * These are <a href="#"> elements. They announce as links, expose no
   * expanded state, and because href="#" is a live fragment the browser
   * jumps the document to the top on activation — a measured 2,929px reset
   * on /collections/all. Convert them to real disclosure buttons.
   * ================================================================== */
  function initFilterDisclosures() {
    document.querySelectorAll('.filter-form a.dropdown').forEach(function (link) {
      if (link.dataset.ctA11y === 'done') return;
      link.dataset.ctA11y = 'done';

      var panel = link.nextElementSibling;

      // Give it button semantics without replacing the node, so the theme's
      // existing jQuery handlers stay bound.
      link.setAttribute('role', 'button');
      link.removeAttribute('href');            // kills the jump-to-top default
      link.setAttribute('tabindex', '0');

      if (panel) {
        if (!panel.id) {
          panel.id = 'ct-filter-panel-' +
            (link.textContent || 'group').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
        }
        link.setAttribute('aria-controls', panel.id);
        var open = window.getComputedStyle(panel).display !== 'none';
        link.setAttribute('aria-expanded', open ? 'true' : 'false');

        // Mirror the theme's own show/hide into aria-expanded.
        new MutationObserver(function () {
          var nowOpen = window.getComputedStyle(panel).display !== 'none';
          link.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
        }).observe(panel, { attributes: true, attributeFilter: ['style', 'class'] });
      }

    });

    /* ---- Tag-based sidebar filters — §33 Nav-10, the pleaded defect ----
     *
     * "Shop By Minimum", "Bags" and "Sustainability" live in a different
     * structure from "Shop By Price". They come from sections/filters.liquid as:
     *
     *   <div class="sidebar-filter shop-by-minimum">
     *     <h4 class="dropdown close">Shop By Minimum</h4>   <- the toggle
     *     <div class="filter-element hide">…radios…</div>
     *
     * The toggle is a HEADING. A heading is not focusable, so Tab skips these
     * three entirely and jumps from "View All" straight to "Shop By Price" —
     * measured: 0 tab stops between them. That is exactly what the complaint
     * describes, and why it only names these two filters as unreachable while
     * conceding "Shop By Price" does receive focus (it is an <a href="#">).
     *
     * Fix: put a real <button> inside the heading. The <h4> keeps its heading
     * semantics for screen-reader heading navigation, and a click on the button
     * still bubbles to the <h4> — which is what the theme's own handler,
     * $('.sidebar-filter .dropdown').click(...) in sections/filters.liquid:99,
     * is bound to. No existing behaviour changes.
     */
    document.querySelectorAll('.sidebar-filter h4.dropdown').forEach(function (heading) {
      if (heading.dataset.ctA11y === 'done') return;
      heading.dataset.ctA11y = 'done';

      var label = (heading.textContent || '').replace(/\s+/g, ' ').trim();
      if (!label) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ct-filter-toggle';
      btn.textContent = label;
      // The theme toggles a `close` class on the heading: present = collapsed.
      btn.setAttribute('aria-expanded', heading.classList.contains('close') ? 'false' : 'true');

      heading.textContent = '';
      heading.appendChild(btn);

      new MutationObserver(function () {
        btn.setAttribute('aria-expanded', heading.classList.contains('close') ? 'false' : 'true');
      }).observe(heading, { attributes: true, attributeFilter: ['class'] });

      // Name the radio group after its filter, and say how it behaves. Without
      // this the options announce as "All, radio button 1 of 6" with no clue
      // which filter they belong to.
      var group = heading.closest('.sidebar-filter');
      if (group && !group.hasAttribute('role')) {
        if (!btn.id) btn.id = 'ct-filter-label-' + ++filterGroupCounter;
        var hintId = btn.id + '-hint';
        var hint = document.createElement('p');
        hint.id = hintId;
        hint.className = 'visually-hidden';
        hint.textContent =
          'Use the up and down arrow keys to choose an option, then press Enter to apply the filter.';
        group.appendChild(hint);

        group.setAttribute('role', 'group');
        group.setAttribute('aria-labelledby', btn.id);
        group.setAttribute('aria-describedby', hintId);
      }
    });

    // The current sort is conveyed only by a CSS class.
    document.querySelectorAll('.sort-by .item a').forEach(function (a) {
      if (a.classList.contains('active')) a.setAttribute('aria-current', 'true');
    });

    // Filter panel controls the theme leaves unnamed.
    document.querySelectorAll('.filter-form input[type="submit"]').forEach(function (el) {
      if (!el.getAttribute('aria-label') && el.value) {
        var group = el.closest('ul.item') || el.closest('li');
        var heading = group ? group.parentElement.querySelector('a.dropdown') : null;
        if (heading) {
          el.setAttribute('aria-label',
            el.value + ' ' + (heading.textContent || '').replace(/\s+/g, ' ').trim() + ' filter');
        }
      }
    });

    // Price range sliders ship with no id, name, label or aria-label.
    var ranges = document.querySelectorAll('.range-slider input[type="range"]');
    if (ranges.length === 2) {
      ranges[0].setAttribute('aria-label', 'Minimum price');
      ranges[1].setAttribute('aria-label', 'Maximum price');
    }
    var gte = document.querySelector('input[name$="price.gte"]');
    var lte = document.querySelector('input[name$="price.lte"]');
    if (gte && !gte.getAttribute('aria-label')) gte.setAttribute('aria-label', 'Minimum price');
    if (lte && !lte.getAttribute('aria-label')) lte.setAttribute('aria-label', 'Maximum price');
  }

  /* Delegated key handling for the filter/sort disclosures.
   *
   * These controls are <a> elements with `href` removed and `role="button"`
   * applied. A link without an href fires click on NEITHER Enter nor Space
   * natively, so both must be handled explicitly — an earlier version covered
   * only Space, which left the filters keyboard-dead on Enter. Delegated
   * because the filter form is re-rendered by the theme's own AJAX. */
  document.addEventListener('keydown', function (event) {
    var control = event.target.closest('.filter-form a.dropdown[role="button"]');
    if (!control) return;
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    event.preventDefault();
    control.click();
  });

  /* ================================================================== *
   * 6. Mobile menu drawer
   *
   * The drawer is stock Dawn markup in sections/header.liquid and has three
   * defects independent of anything above:
   *
   *   a. A parent item is a bare <summary> containing only the link's title —
   *      there is no <a href> anywhere for the parent's own page, so "Shop",
   *      "Kits" etc. are unreachable on mobile. Some menus happen to repeat the
   *      parent as a child link; most do not.
   *
   *   b. MenuDrawer.addAccessibilityAttributes() sets aria-expanded="false" on
   *      every <summary> (global.js:356-362), but onSummaryClick only updates it
   *      in the main-toggle branch (:394). The submenu branch (:381-387) never
   *      sets it and closeSubmenu (:424) never resets it, so every submenu
   *      announces "collapsed" permanently — the same defect the complaint
   *      pleads for the desktop nav at §33 Nav-3.
   *
   *   c. closeSubmenu() defers removing [open] to closeAnimation(), which runs a
   *      requestAnimationFrame loop for 400ms. If rAF is throttled or never
   *      fires, [open] is never removed and the submenu stays open.
   * ================================================================== */
  function initMenuDrawer() {
    var drawer = document.querySelector('header-drawer, menu-drawer');
    if (!drawer) return;

    drawer.querySelectorAll('.menu-drawer__menu li > details').forEach(function (details) {
      var summary = details.querySelector(':scope > summary');
      var panel = details.querySelector(':scope > .menu-drawer__submenu');
      if (!summary || !panel || details.dataset.ctDrawer === 'done') return;
      details.dataset.ctDrawer = 'done';

      // (b) Keep aria-expanded honest by watching the [open] attribute, which
      // is the single source of truth regardless of which code path changed it.
      var sync = function () {
        summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
      };
      new MutationObserver(sync).observe(details, { attributes: true, attributeFilter: ['open'] });
      sync();

      // The back button's aria-expanded is hardcoded "true" in the template.
      // It closes the panel, so it is not a disclosure for it — drop the lie.
      var back = panel.querySelector('.menu-drawer__close-button');
      if (back) back.removeAttribute('aria-expanded');

      // (a) Give the parent page a real link, as the first item of its own
      // submenu. Skipped when the merchant already repeated it as a child.
      var list = panel.querySelector('ul.menu-drawer__menu');
      var parentUrl = details.dataset.ctParentUrl || null;
      if (list && parentUrl && !list.querySelector('a[href="' + parentUrl + '"]')) {
        var label = (summary.textContent || '').replace(/\s+/g, ' ').trim();
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = parentUrl;
        a.className = 'menu-drawer__menu-item link link--text list-menu__item focus-inset ct-parent-link';
        a.textContent = 'All ' + label;
        li.appendChild(a);
        list.insertBefore(li, list.firstChild);
      }
    });
  }

  // (c) rAF-independent safety net: if the theme's close animation has not
  // removed [open] shortly after a close button is pressed, remove it here.
  document.addEventListener('click', function (event) {
    var back = event.target.closest('.menu-drawer__close-button');
    if (!back) return;
    var details = back.closest('details');
    if (!details) return;
    window.setTimeout(function () {
      if (details.hasAttribute('open')) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      }
    }, 500);
  });

  /* ================================================================== *
   * 7. Minimum-quantity conveyance and enforcement — §33 Qty
   *
   * The input is min="24" with a visible label of "Minimum Quantity", but
   * nothing tells a screen reader what the minimum IS, and typing a lower
   * value produces no error and still adds to the cart. That is both a
   * WCAG 3.3.1/3.3.3 failure and an order-integrity bug.
   * ================================================================== */
  /* ================================================================== *
   * Tag-based filter radio groups — WCAG 3.2.2 (On Input)
   *
   * sections/filters.liquid:78-98 wires this chain:
   *
   *   radio `change` -> copy value to the hidden <select> -> `change` on the
   *   select -> window.location.href = <filtered collection URL>
   *
   * So *any* change navigates. Arrow keys change a radio, which means a
   * keyboard user cannot move past the first option — the page reloads on the
   * first arrow press and their place is lost. Reaching the sixth option would
   * take five full page loads.
   *
   * WCAG 3.2.2 requires that changing the setting of a component not
   * automatically change context unless the user was told beforehand.
   *
   * Mouse behaviour is deliberately unchanged: a click still filters instantly.
   * Only arrow-key movement is intercepted, and Enter commits the choice. The
   * theme's handlers are delegated on <body>, so a capture-phase listener on
   * document runs first and can stop the event before it ever reaches them.
   * ================================================================== */
  /* A TIMESTAMP, not a boolean flag.
   *
   * An earlier version armed a boolean on arrow keydown and cleared it on
   * `focusout`. That could never work: moving between radios with an arrow key
   * IS a focus move, so the browser's own default action fired
   *
   *   keydown(A) -> focusout(A) -> focusin(B) -> input(B) -> change(B)
   *
   * and the focusout cleared the flag before the change arrived. Nothing was
   * ever suppressed. A timestamp is immune to focus churn entirely. */
  var lastArrowKeyAt = -1e9;
  var ARROW_WINDOW_MS = 400;

  function eventTime(event) {
    // change and keydown share the same time origin, so they are comparable.
    return typeof event.timeStamp === 'number' ? event.timeStamp : performance.now();
  }

  function cameFromArrowKey(event) {
    return eventTime(event) - lastArrowKeyAt < ARROW_WINDOW_MS;
  }

  function commitFilterRadio(radio) {
    lastArrowKeyAt = -1e9;                 // this commit must not be suppressed
    radio.dataset.ctCommit = 'true';
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('keydown', function (event) {
    var radio = event.target.closest && event.target.closest('.sidebar-filter input[type="radio"]');
    if (!radio) return;

    if (event.key.indexOf('Arrow') === 0) {
      lastArrowKeyAt = eventTime(event);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commitFilterRadio(radio);
    }
  }, true);

  // A pointer interaction is an explicit choice — disarm so it passes through.
  document.addEventListener('pointerdown', function () {
    lastArrowKeyAt = -1e9;
  }, true);

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var inFilter =
      target.closest('.sidebar-filter input[type="radio"]') ||
      // Second line of defence: the theme copies the radio's value onto a hidden
      // <select> and re-fires change on THAT, and it is the select handler that
      // calls window.location.href. Block both ends of the chain.
      target.closest('.sidebar-filter select');
    if (!inFilter) return;

    var radio = target.closest('.sidebar-filter input[type="radio"]');
    if (radio && radio.dataset.ctCommit === 'true') {
      delete radio.dataset.ctCommit;
      return;
    }
    if (!cameFromArrowKey(event)) return;   // click or programmatic — unchanged

    // Arrow-key movement: the radio stays visually selected and announced, but
    // the filter is not applied and the page does not reload until Enter.
    event.stopImmediatePropagation();
    event.preventDefault();
  }, true);

  /* ================================================================== *
   * 8. Slick's fake tab interface, and unnamed background-image logos
   *
   * Slick's accessibility option wraps every slide in
   *   <div class="slick-slide" role="tabpanel" tabindex="0" aria-describedby="...">
   * A carousel of cards is not a tab interface. To a screen reader every slide
   * becomes an extra, meaningless tab stop announcing "tab panel" (or "group"),
   * immediately followed by the real link inside it repeating the same words.
   * Measured on the homepage: 23 such wrappers.
   *
   * That single defect produced all of these reports:
   *   - client logos announcing "tab panel" instead of a brand name
   *   - "Shop Clothing" announced as a group, then again as a link
   *   - the hero and "Explore Moment" photos announcing a bare "group"
   *
   * Slides keep Slick's aria-hidden (which correctly hides off-screen ones) and
   * arrow-key navigation is untouched — only the bogus tab semantics go.
   *
   * The client logos are a second problem: they are CSS background-images on
   * empty <div>s, so there is no alt attribute to fill and nothing at all
   * reaches assistive tech. role="img" plus a name derived from the filename
   * gives them a voice. That derivation is a fallback, not a substitute for real
   * alt text — see PATCHES.md.
   * ================================================================== */
  function nameFromImageUrl(url) {
    if (!url) return '';
    var file = url.replace(/['"()]/g, '').split('?')[0].split('/').pop();
    return file
      .replace(/\.(png|jpe?g|webp|svg|gif)$/i, '')                                    // extension
      .replace(/_\d+x\d*$/i, '')                                                      // Shopify _300x
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '') // uuid
      .replace(/_[0-9a-f]{16,}$/i, '')                                                // long hex run
      .replace(/[-_]+\d+$/, '')                                                       // _1 dedupe suffix
      .replace(/[-_]+/g, ' ')
      .trim()
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function stripFakeTabSemantics(root) {
    root.querySelectorAll('.slick-slide').forEach(function (slide) {
      if (slide.getAttribute('role') === 'tabpanel') slide.removeAttribute('role');
      // A div is not focusable without tabindex, so removing the attribute
      // (rather than setting -1) takes the slide out of the tab order entirely.
      if (slide.hasAttribute('tabindex')) slide.removeAttribute('tabindex');
      if (slide.hasAttribute('aria-describedby')) slide.removeAttribute('aria-describedby');
    });
  }

  function nameBackgroundImageLogos(root) {
    root
      .querySelectorAll('.logos [style*="background-image"], .corporate-logo [style*="background-image"]')
      .forEach(function (el) {
        if (el.dataset.ctNamed === 'done') return;
        // corporate-section.liquid now emits role="img" with the image's real
        // alt text from Shopify. That is always better than a filename guess, so
        // never overwrite it — only fill the gap where alt was left blank.
        if ((el.getAttribute('aria-label') || '').trim() || el.getAttribute('role') === 'img') {
          el.dataset.ctNamed = 'done';
          return;
        }
        var m = (el.getAttribute('style') || '').match(/url\(([^)]+)\)/);
        var name = nameFromImageUrl(m && m[1]);
        if (!name) return;
        el.dataset.ctNamed = 'done';
        el.setAttribute('role', 'img');
        el.setAttribute('aria-label', name);
      });
  }

  /* Slick marks every off-screen slide aria-hidden="true", which removes it
   * from the accessibility tree entirely. Measured on the homepage: 16 of 18
   * client logos, 7 of 8 testimonials and 2 of 3 blog cards were unreachable —
   * a screen reader could not read past whatever happened to be on screen, and
   * several sections were effectively skipped.
   *
   * Exposing them is safe because Slick already sets tabindex="-1" on every
   * link inside an off-screen slide (verified across all five multi-slide
   * carousels: zero off-screen links remained tabbable). So the content becomes
   * readable without adding a single phantom tab stop, and Slick restores
   * tabindex="0" as slides come into view.
   *
   * Slick duplicate clones stay hidden, or every item would be announced twice.
   *
   * NOTE these are read in the screen reader browse cursor, not with Tab. Tab
   * visits interactive controls only; images and text are reached with the
   * arrow keys. An earlier attempt to advance the carousel to whichever slide
   * took focus was REVERTED — it scrolled cards out from under the user and
   * made shift-tab feel broken.
   */
  function exposeStaticSlides(root) {
    root.querySelectorAll('.slick-initialized').forEach(function (slider) {
      var slides = slider.querySelectorAll('.slick-slide');
      if (!slides.length) return;

      slides.forEach(function (slide) {
        if (slide.classList.contains('slick-cloned')) {
          if (slide.getAttribute('aria-hidden') !== 'true') {
            slide.setAttribute('aria-hidden', 'true');
          }
        } else if (slide.getAttribute('aria-hidden') === 'true') {
          slide.removeAttribute('aria-hidden');
        }
      });
    });
  }

  /* ================================================================== *
   * 10. Generic link text — §33 Nav-7, §30  ·  WCAG 2.4.4
   *
   * The blog cards each expose only "Read More >". The article title sits in an
   * <h3> right beside the link but is not part of its accessible name, so a
   * screen reader's link list shows "Read More" three times with nothing to
   * tell them apart. That is the pleaded defect: "Generic, non-descriptive
   * links such as 'See more link', 'Read more link' (announced three times),
   * and 'View all link'."
   *
   * The visible text is left alone — only a visually-hidden suffix is appended,
   * so the design does not change but the link announces
   * "Read More, Summer Corporate Gifting: The Off-Season Advantage".
   * ================================================================== */
  var GENERIC_LINK_TEXT = /^(read|learn|see|view|shop|explore)\s*(more|all|now)?\s*[>›»]?$/i;

  function contextualiseGenericLinks(root) {
    root.querySelectorAll('a[href]').forEach(function (link) {
      if (link.dataset.ctCtx === 'done') return;

      var text = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (!GENERIC_LINK_TEXT.test(text)) return;
      if ((link.getAttribute('aria-label') || '').trim()) return;

      // Prefer the heading of the card the link belongs to; fall back to the
      // section heading for things like a standalone "View All".
      var card = link.closest('.slick-slide, article, li, .card, .grid__item, .blog__post');
      var heading = card && card.querySelector('h2, h3, h4, h5');
      if (!heading) {
        var section = link.closest('.shopify-section');
        heading = section && section.querySelector('h2, h3');
      }
      var context = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!context || context.toLowerCase() === text.toLowerCase()) return;

      link.dataset.ctCtx = 'done';
      var span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = ' ' + context;
      link.appendChild(span);
    });
  }

  /* ================================================================== *
   * 11. Empty aria-label, and links that are only a background image
   *
   * aria-label="" is worse than no label at all: it forces an empty accessible
   * name, so the reader falls back to the URL. On the suppliers strip one of
   * twelve brand links carries an empty label, which is why it announces
   * "brands" (from /pages/brands) while its neighbours announce their brand.
   *
   * Removing the empty attribute lets the normal name computation run; where
   * that still yields nothing and the link is just a CSS background image, the
   * filename fallback from section 8 applies.
   * ================================================================== */
  function fixEmptyAndImageOnlyLinks(root) {
    root.querySelectorAll('a[aria-label=""], button[aria-label=""]').forEach(function (el) {
      el.removeAttribute('aria-label');
    });

    root.querySelectorAll('a[href]').forEach(function (link) {
      if (link.dataset.ctNamedLink === 'done') return;

      var named = (link.getAttribute('aria-label') || '').trim() ||
        (link.textContent || '').replace(/\s+/g, ' ').trim() ||
        Array.prototype.map.call(link.querySelectorAll('img'), function (i) {
          return i.getAttribute('alt') || '';
        }).join(' ').trim();
      if (named) return;

      var bg = link.querySelector('[style*="background-image"]');
      if (!bg && (link.getAttribute('style') || '').indexOf('background-image') !== -1) bg = link;
      if (!bg) return;

      var m = (bg.getAttribute('style') || '').match(/url\(([^)]+)\)/);
      var name = nameFromImageUrl(m && m[1]);
      if (!name) return;

      link.dataset.ctNamedLink = 'done';
      link.setAttribute('aria-label', name);
    });
  }


  function initSliderSemantics() {
    var sliders = document.querySelectorAll('.slick-initialized');
    if (!sliders.length) return;

    stripFakeTabSemantics(document);
    nameBackgroundImageLogos(document);
    exposeStaticSlides(document);
    contextualiseGenericLinks(document);
    fixEmptyAndImageOnlyLinks(document);

    sliders.forEach(function (slider) {
      if (slider.dataset.ctSliderObs === 'done') return;
      slider.dataset.ctSliderObs = 'done';
      // Slick re-applies the tab markup on every slide change, so watch for it.
      // Removing an attribute re-triggers this observer, but the second pass
      // finds nothing left to remove and it settles.
      new MutationObserver(function () {
        stripFakeTabSemantics(slider);
        nameBackgroundImageLogos(slider);
        exposeStaticSlides(slider.parentElement || slider);
        contextualiseGenericLinks(slider);
        fixEmptyAndImageOnlyLinks(slider);
      }).observe(slider, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['role', 'tabindex', 'aria-describedby', 'aria-hidden']
      });
    });
  }

  /* ================================================================== *
   * 9. Auto-playing decorative video — WCAG 2.2.2 and 4.1.2
   *
   * The homepage hero video is
   *   <video autoplay loop muted playsinline controls="hidden" tabindex="0">
   * with pointer-events:none. Three problems:
   *
   *   - tabindex="0" with no accessible name: a tab stop announcing only "group"
   *   - autoplay + loop on a 15s clip is moving content lasting more than five
   *     seconds with no way to stop it (2.2.2)
   *   - controls="hidden" does nothing useful. `controls` is a boolean
   *     attribute, so any value enables it — and pointer-events:none then makes
   *     those controls unusable anyway.
   *
   * Treated as decorative: hidden from assistive tech and out of the tab order,
   * with a real pause control and reduced-motion respected.
   * ================================================================== */
  function initAutoplayVideos() {
    document.querySelectorAll('video[autoplay]').forEach(function (video) {
      if (video.dataset.ctA11y === 'done') return;
      video.dataset.ctA11y = 'done';

      var named = (video.getAttribute('aria-label') || '').trim() ||
        (video.getAttribute('title') || '').trim();
      if (!named) {
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
      }

      // Only content that actually keeps moving past five seconds needs a
      // pause mechanism.
      if (!video.loop && isFinite(video.duration) && video.duration < 5) return;

      var section = video.closest('.shopify-section') || video.parentElement;
      if (!section || section.querySelector('.ct-carousel-pause[data-ct-video]')) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ct-carousel-pause';
      btn.dataset.ctVideo = 'true';
      btn.dataset.state = 'playing';
      btn.setAttribute('aria-label', 'Pause background video');
      btn.innerHTML = '<span class="ct-carousel-pause__icon" aria-hidden="true"></span>' +
        '<span class="ct-carousel-pause__text">Pause video</span>';

      function setState(playing) {
        btn.dataset.state = playing ? 'playing' : 'paused';
        btn.setAttribute('aria-label', (playing ? 'Pause' : 'Play') + ' background video');
        btn.querySelector('.ct-carousel-pause__text').textContent =
          playing ? 'Pause video' : 'Play video';
      }

      btn.addEventListener('click', function () {
        if (btn.dataset.state === 'playing') {
          video.pause();
          setState(false);
          announce('Background video paused');
        } else {
          video.play();
          setState(true);
          announce('Background video playing');
        }
      });
      section.appendChild(btn);

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        video.pause();
        setState(false);
      }
    });
  }

  function initMinimumQuantity() {
    document.querySelectorAll('input[name="quantity"][min]').forEach(function (input) {
      var min = parseInt(input.getAttribute('min'), 10);
      if (!min || min <= 1 || input.dataset.ctA11y === 'done') return;

      // Leave the Kit Builder alone. sections/kit-builder-cart.liquid has its
      // own quantity input inside <form id="kitbuilder-main"> and enforces the
      // same minimum in its own JS. Attaching a capture-phase submit blocker
      // here would pre-empt that flow via stopImmediatePropagation() for no
      // benefit. Its quantity UX needs a separate, dedicated review.
      if (input.closest('#kitbuilder-main, .kit--collections-checkout, [class*="kit-builder"]')) return;

      input.dataset.ctA11y = 'done';

      // IDs must be unique per input — main-product.liquid alone renders three
      // quantity inputs, and a shared id would make every one of them point at
      // the first input's hint.
      var uid = ++minQtyCounter;
      var hintId = 'ct-min-qty-hint-' + uid;
      var errId = 'ct-min-qty-error-' + uid;

      // Never append into the input itself. On the Kit Builder cart the input
      // *carries* .product-form__input, so closest() would return the input and
      // the hint would be parked inside a void element where nothing can see it.
      var host = input.parentNode;
      var wrapper = input.closest('.product-form__input');
      if (wrapper && wrapper !== input) host = wrapper;

      // The hint is SCREEN-READER ONLY.
      //
      // Sighted users already have the visible "Minimum Quantity" label and the
      // pre-filled value, so a visible sentence just repeats them — and the
      // quantity field sits in a narrow column beside Add To Cart, which
      // overlapped the extra line. What was actually missing was the
      // *programmatic* association, which aria-describedby supplies without
      // adding anything to the layout.
      var hint = document.createElement('span');
      hint.id = hintId;
      hint.className = 'ct-qty-hint visually-hidden';
      hint.textContent = 'Minimum order quantity is ' + min + '.';
      host.appendChild(hint);

      input.setAttribute('aria-describedby',
        ((input.getAttribute('aria-describedby') || '') + ' ' + hintId).trim());

      /* The ERROR must stay visible — an error only sighted users cannot see is
       * a 3.3.1 failure in the other direction. Rather than add a second block
       * to that cramped column, reuse the theme's own error region: Dawn already
       * renders .product-form__error-message-wrapper with role="alert" and an
       * icon, positioned properly, and product-form.js uses it for add-to-cart
       * failures. Fall back to our own element if it is absent. */
      var scope = input.closest('.shopify-section') || document;
      var themeErrWrap = scope.querySelector('.product-form__error-message-wrapper');
      var themeErrMsg = themeErrWrap && themeErrWrap.querySelector('.product-form__error-message');

      var err = null;
      if (!themeErrMsg) {
        err = document.createElement('p');
        err.id = errId;
        err.className = 'ct-qty-error';
        err.setAttribute('role', 'alert');
        err.hidden = true;
        hint.insertAdjacentElement('afterend', err);
      }

      var MESSAGE = 'Please enter ' + min + ' or more. ' +
        min + ' is the minimum order quantity for this item.';

      function showError(show) {
        if (themeErrMsg) {
          if (show) {
            themeErrMsg.textContent = MESSAGE;
            themeErrWrap.removeAttribute('hidden');
            themeErrWrap.dataset.ctOwned = 'true';
          } else if (themeErrWrap.dataset.ctOwned === 'true') {
            // Only clear it if WE put the message there — never wipe a genuine
            // add-to-cart error the theme raised.
            themeErrMsg.textContent = '';
            themeErrWrap.setAttribute('hidden', '');
            delete themeErrWrap.dataset.ctOwned;
          }
          return;
        }
        err.hidden = !show;
        err.textContent = show ? MESSAGE : '';
      }

      function validate() {
        var value = parseInt(input.value, 10);
        var bad = !isNaN(value) && value < min;
        input.setAttribute('aria-invalid', bad ? 'true' : 'false');
        showError(bad);
        return !bad;
      }

      input.addEventListener('input', validate);
      input.addEventListener('blur', validate);

      // Block the AJAX add-to-cart path, which bypasses native validation.
      // On this theme the quantity input is NOT a DOM descendant of the
      // add-to-cart form — it associates via the HTML5 `form` attribute, so
      // closest('form') returns null. input.form honours that association.
      var form = input.form || input.closest('form');
      if (form) {
        form.addEventListener('submit', function (event) {
          if (!validate()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            input.focus();
          }
        }, true);
      }
    });
  }

  /* ================================================================== *
   * Stylesheet guard
   *
   * This script and ct-a11y.css are a pair. Without the stylesheet the
   * submenu toggles inherit the browser's default button chrome — grey box,
   * outset border — and wrap onto their own line, because the theme has no
   * base `button` reset and the nav link is a full-width flex item.
   * Fail loudly rather than shipping visible breakage.
   * ================================================================== */
  var stylesheetChecked = false;

  function assertStylesheetLoaded() {
    if (stylesheetChecked) return true;   // init() runs several times; warn once
    stylesheetChecked = true;

    var probe = document.createElement('button');
    probe.className = 'ct-submenu-toggle';
    probe.style.cssText = 'position:absolute;left:-9999px;top:-9999px';
    document.body.appendChild(probe);
    var bg = window.getComputedStyle(probe).backgroundColor;
    var transparent = bg === 'transparent' || /rgba\(0,\s*0,\s*0,\s*0\)/.test(bg);
    probe.remove();

    if (!transparent) {
      console.error(
        '[ct-a11y] ct-a11y.css is not loaded — submenu toggles will render as ' +
        'grey default buttons below each menu item. Add the asset and confirm ' +
        'layout/theme.liquid contains: ' +
        "{{ 'ct-a11y.css' | asset_url | stylesheet_tag }}"
      );
      return false;
    }
    return true;
  }

  function init() {
    assertStylesheetLoaded();
    // The header nav is cloned into a placeholder by jQuery on ready, so run
    // against the whole document and guard with a data flag.
    initNav();
    initMenuDrawer();
    initFilterDisclosures();
    initLoadMore();
    initMinimumQuantity();
    initCarouselPauseControls();
    initSliderSemantics();
    initAutoplayVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // navigation.liquid clones the menu into the header after ready; re-run once
  // the clone lands, and after Slick finishes initialising.
  window.setTimeout(init, 600);
  window.setTimeout(init, 1800);

  if (window.jQuery) {
    window.jQuery(document).on('shopify:section:load', init);
  }
})();